import { initAccordion, initAccordionContainer } from './modules/accordion';
import { init as initFormFilters } from './modules/form-filters';

const ACCORDION_INITED_ATTR = 'data-cdv-accordion-inited';
const MODAL_SHOWN_CLASS = 'modal-shown';

/**
 * Remet la « page » côté URL pour ALM :
 * - `pg` (addon filtres)
 * - segment WordPress `/page/N/` (addon SEO + paging), ex. /pays/italie/page/2/
 */
function resetAlmPagingToFirstPageInUrl() {
    try {
        const url = new URL(window.location.href);
        let changed = false;

        if (url.searchParams.has('pg')) {
            url.searchParams.delete('pg');
            changed = true;
        }

        const withoutPaged = url.pathname.replace(/\/page\/\d+\/?$/, '/');
        if (withoutPaged !== url.pathname) {
            url.pathname = withoutPaged.replace(/\/+/g, '/');
            changed = true;
        }

        if (!changed) return;
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch {
        /* URL ou history non disponible */
    }
}

/**
 * Paramètres de requête posés par ALM (modal + bandeau envies, etc.) à retirer après réinit complète.
 * Inclut notamment `relations_envies` (radio hors modal).
 */
const ALM_FILTER_RESET_QUERY_KEYS = [
    'duree_nombre_de_jours',
    'prix_fourchette_basse',
    'relations_envies',
    'relations_pays',
    'relations_regions',
    'relations_villes',
    'relations_types',
    'relations_pois',
    'cible',
    'periode'
];

function stripAlmFilterParamsFromUrl() {
    try {
        const url = new URL(window.location.href);
        let changed = false;
        ALM_FILTER_RESET_QUERY_KEYS.forEach((key) => {
            if (url.searchParams.has(key)) {
                url.searchParams.delete(key);
                changed = true;
            }
        });
        if (!changed) return;
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch {
        /* ignore */
    }
}

/**
 * Nombre de paramètres de filtres ALM actifs dans l’URL (ex. `?relations_envies=…`).
 * Sert quand ALM ne remplit pas #alm-selected-filters (p. ex. plusieurs instances de grille).
 */
function countAlmFilterParamsInUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        let n = 0;
        ALM_FILTER_RESET_QUERY_KEYS.forEach((key) => {
            const v = params.get(key);
            if (v != null && String(v).trim() !== '') {
                n += 1;
            }
        });
        return n;
    } catch {
        return 0;
    }
}

/**
 * Filtre radio du bandeau envies (hors modal) : envie / cible sélectionné(e).
 * Exclut l’option « tout » (data-type=all).
 */
function hasActiveEnviesStripSelection() {
    const bar = document.querySelector('.cdv-envies-filters-bar');
    if (!bar) {
        return false;
    }
    const active = bar.querySelector('.alm-filter--link.active');
    if (!active) {
        return false;
    }
    if (active.getAttribute('data-type') === 'all') {
        return false;
    }
    const v = active.getAttribute('data-value') ?? active.dataset?.value;
    if (v == null) {
        return false;
    }
    const s = String(v).trim();
    if (s === '' || s === '#') {
        return false;
    }
    return true;
}

function safeJsonParse(raw, fallback) {
    if (!raw) return fallback;
    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    initPopupFiltre();
});

function initPopupFiltre() {
    const modalFilter = document.getElementById('modal-filter');
    if (!modalFilter) return;

    const filterContainer = document.getElementById('accordion-filtre');
    const modalAlmGroups = modalFilter.querySelectorAll('.alm-filter');
    const usesLegacyAccordion = Boolean(filterContainer);
    /** Modal destinations + bandeau envies (radio hors modal) : tous pilotés par ALM. */
    const usesAlmFilters =
        modalAlmGroups.length > 0 ||
        document.querySelectorAll('.cdv-envies-filters-bar .alm-filter').length > 0;

    const originalFiltersHtml = filterContainer ? filterContainer.innerHTML : '';

    const products = safeJsonParse(modalFilter.getAttribute('data-products'), []);
    const list = safeJsonParse(modalFilter.getAttribute('data-list'), {});
    const aLaUne = safeJsonParse(modalFilter.getAttribute('data-a-la-une'), {});

    const size = document.querySelectorAll('.ac-panel .ac-list').length;
    const resetFilter = document.querySelector('.reset-filter');
    const listFiltre = document.querySelector('.filtre-name');
    const swiperControls = document.querySelector('.swiper__controls-filters');

    let ajaxTimer;

    /**
     * Réinitialise les groupes ALM du modal via l’API officielle.
     *
     * @see https://connekthq.com/plugins/ajax-load-more/docs/filter-hooks/ — `almfilters.resetFilter(filterElement)`
     */
    function resetAlmRangeSlidersExplicit() {
        modalFilter.querySelectorAll('.alm-filter[data-fieldtype="range_slider"] .alm-range-reset').forEach((btn) => {
            btn.click();
        });
    }

    function triggerAlmModalSubmitIfPresent() {
        const btn = modalFilter.querySelector('.alm-filters--submit .alm-filters--button');
        if (btn) {
            btn.click();
        }
    }

    /**
     * Groupes ALM pour la grille : filtres du modal + bandeau envies/cibles (radio `relations_envies`), hors modal.
     */
    function collectAlmFilterGroupElements() {
        const seen = new Set();
        /** @type {Element[]} */
        const out = [];
        document.querySelectorAll('#modal-filter .alm-filter, .cdv-envies-filters-bar .alm-filter').forEach((el) => {
            if (!seen.has(el)) {
                seen.add(el);
                out.push(el);
            }
        });
        return out;
    }

    function resetAlmModalFiltersViaPlugin() {
        const api = window.almfilters;
        const groups = collectAlmFilterGroupElements();

        if (!api || typeof api.resetFilter !== 'function') {
            resetAlmRangeSlidersExplicit();
            triggerAlmModalSubmitIfPresent();
            stripAlmFilterParamsFromUrl();
            resetAlmPagingToFirstPageInUrl();
            return;
        }

        if (groups.length === 0) {
            stripAlmFilterParamsFromUrl();
            resetAlmPagingToFirstPageInUrl();
            return;
        }

        groups.forEach((el) => api.resetFilter(el));
        resetAlmRangeSlidersExplicit();
        triggerAlmModalSubmitIfPresent();
        stripAlmFilterParamsFromUrl();
        resetAlmPagingToFirstPageInUrl();

        if (typeof window.cdvRebuildEnviesFiltersSwiper === 'function') {
            requestAnimationFrame(() => window.cdvRebuildEnviesFiltersSwiper());
        }
    }

    if (filterContainer) {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class' && filterContainer) {
                    if (modalFilter.classList.contains(MODAL_SHOWN_CLASS)) {
                        initAccordionContainer(filterContainer);
                    }
                }
            });
        });
        observer.observe(modalFilter, { attributes: true, attributeFilter: ['class'] });
    }

    function reinitAccordion() {
        if (!filterContainer) return;
        filterContainer.removeAttribute(ACCORDION_INITED_ATTR);
        initAccordion();
        initAccordionContainer(filterContainer);
    }

    function observeAlmSelectedStrip() {
        const wrap = document.getElementById('alm-selected-filters');
        if (!wrap || wrap.getAttribute('data-cdv-reset-strip-observer') === '1') {
            return;
        }
        wrap.setAttribute('data-cdv-reset-strip-observer', '1');
        let debounce = null;
        const obs = new MutationObserver(() => {
            if (debounce !== null) {
                clearTimeout(debounce);
            }
            debounce = setTimeout(() => {
                debounce = null;
                updateResetFilterVisibility();
                if (typeof window.cdvRefreshSelectedFiltersStrip === 'function') {
                    window.cdvRefreshSelectedFiltersStrip();
                }
            }, 25);
        });
        obs.observe(wrap, { childList: true, subtree: true });
    }
    observeAlmSelectedStrip();

    document.addEventListener(
        'click',
        (e) => {
            if (e.target && typeof e.target.closest === 'function' && e.target.closest('.cdv-envies-filters-bar')) {
                requestAnimationFrame(() => {
                    updateResetFilterVisibility();
                });
            }
        },
        true
    );
    window.addEventListener('popstate', () => {
        updateResetFilterVisibility();
    });

    updateResetFilterVisibility();

    if (resetFilter) {
        resetFilter.addEventListener('click', (e) => {
            e.preventDefault();
            resetFilters();
        });
    }

    const paysClass = '.checkbox-pays';
    const regionsClass = '.checkbox-regions';
    const typeClass = '.checkbox-type';
    const poiClass = '.checkbox-poi';
    const envieClass = '.checkbox-envie';
    const villesClass = '.checkbox-villes';
    const etatsClass = '.checkbox-etats';
    const ciblesClass = '.checkbox-cible';

    const buttons = document.getElementsByClassName('btn-filter-search');
    if (usesLegacyAccordion) {
        Array.from(buttons).forEach((button) => {
            button.addEventListener('click', () => {
                clearTimeout(ajaxTimer);
                ajaxTimer = setTimeout(() => {
                    loadProduct();
                }, 500);
            });
        });
    }

    /**
     * Valeurs pour ALM `vars` (format key:value;…) — pas de ':' ni ';' dans la valeur.
     * @param {string} str
     * @returns {string}
     */
    function sanitizeAlmVarsValue(str) {
        return String(str).replace(/[:;]/g, ' ').replace(/\s+/g, ' ').trim();
    }

    function loadProduct() {
        console.log('Loading products with filters…');
        resetAlmPagingToFirstPageInUrl();

        const pays = Array.from(document.querySelectorAll(paysClass + ':checked')).map((el) => el.value);
        const regions = Array.from(document.querySelectorAll(regionsClass + ':checked')).map((el) => el.value);
        const type = Array.from(document.querySelectorAll(typeClass + ':checked')).map((el) => el.value);
        const typeLabels = Array.from(document.querySelectorAll(typeClass + ':checked'))
            .map((el) => {
                const label = el.closest('.ac-list')?.querySelector('label')?.textContent?.trim();
                return label || el.getAttribute('name') || '';
            })
            .map(sanitizeAlmVarsValue)
            .filter(Boolean);
        const poi = Array.from(document.querySelectorAll(poiClass + ':checked')).map((el) => el.value);
        const envies = Array.from(document.querySelectorAll(envieClass + ':checked')).map((el) => el.value);
        const villes = Array.from(document.querySelectorAll(villesClass + ':checked')).map((el) => el.value);
        const etats = Array.from(document.querySelectorAll(etatsClass + ':checked')).map((el) => el.value);
        const cibles = Array.from(document.querySelectorAll(ciblesClass + ':checked')).map((el) => el.value);

        const data = {
            pays,
            regions,
            type,
            poi,
            envies,
            villes,
            etats,
            cibles,
            list: products,
            filters: list,
            aLaUne: aLaUne
        };

        const productGrid = document.getElementById('product-grid');
        const noProduct = document.querySelector('.no-product');
        const loaderFiltre = document.querySelector('.loader-filtre');

        if (productGrid) productGrid.style.display = 'none';
        if (noProduct) noProduct.style.display = 'none';
        if (loaderFiltre) loaderFiltre.style.display = 'block';

        fetch('/wp-json/wp/v2/filter_v2', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
            .then((response) => response.json())
            .then((response) => {
                if (loaderFiltre) loaderFiltre.style.display = 'none';
                let itemsDataLayer = [];

                const payload = response.data || {};
                const idRaw = payload.id;
                const idsArr = Array.isArray(idRaw) ? idRaw : idRaw != null ? Object.values(idRaw) : [];
                const countProducts =
                    typeof payload.length === 'number' && !Number.isNaN(payload.length)
                        ? payload.length
                        : idsArr.filter((id) => String(id).trim() !== '').length;

                if (response.success && countProducts > 0) {
                    const ids = idsArr;
                    let voyages = [];

                    const nextData = {};
                    nextData.postIn = ids.join(', ');
                    nextData.vars =
                        typeLabels.length > 0 ? `cdv_type_filter_label:${typeLabels.join(', ')}` : '';
                    nextData.seoStartPage = 1;
                    if (typeof window.ajaxloadmore !== 'undefined' && window.ajaxloadmore.filter) {
                        window.ajaxloadmore.filter('fade', 250, nextData);
                    }

                    const numberElement = document.querySelector('.number');
                    if (numberElement) numberElement.textContent = countProducts;
                    const voyageResultEl = document.querySelector('.voyage-result[data-real-count]');
                    if (voyageResultEl) voyageResultEl.dataset.realCount = String(countProducts);

                    if (productGrid) productGrid.style.display = 'block';

                    fetch(`/wp-json/cdv/v2/voyage?ids=${ids.join(',')}`)
                        .then(res => res.json())
                        .then(payload => {
                            voyages = (payload && payload.voyages) ? payload.voyages : [];

                            itemsDataLayer = voyages.slice(0, 10).map((voyage) => ({
                                item_id: voyage.id,
                                item_name: voyage.title,
                                price: Number(voyage.price_min_pp) || 0,
                                quantity: 1,
                                item_category: voyage.city_name,
                                item_category2: voyage.region_name,
                                item_category3: voyage.country_name,
                                item_category4: voyage.continent_name,
                                city_id: voyage.city_id,
                                region_id: voyage.region_id,
                                country_id: voyage.country_id,
                                continent_id: voyage.continent_id,
                                price_min_pp: Number(voyage.price_min_pp) || 0,
                                price_max_pp: Number(voyage.price_max_pp) || 0,
                                trip_duration_days: voyage.trip_duration_days || 0,
                                trip_duration_nights: voyage.trip_duration_nights || 0,
                                item_brand: 'Cercle des Voyages',
                                item_variant: voyage.item_variant || '',
                                theme: voyage.theme || '',
                                advisor_name: voyage.advisor_name || '',
                                itinerary_sample: Array.isArray(voyage.itinerary_sample) ? voyage.itinerary_sample : []
                            }));
                            window.dataLayer = window.dataLayer || [];
                            if (typeof getCheckedFilters === 'function') {
                                dataLayer.push({
                                    event: 'search',
                                    ecommerce: {
                                        currency: 'EUR',
                                        filters: getCheckedFilters(window.cdvVoyagePageFilters),
                                        items: itemsDataLayer
                                    },
                                    _clear: true
                                });
                            }
                        });
                } else {
                    if (noProduct) noProduct.style.display = 'block';
                    if (productGrid) productGrid.style.display = 'none';

                    const numberElement = document.querySelector('.number');
                    if (numberElement) numberElement.textContent = 0;
                    const voyageResultElEmpty = document.querySelector('.voyage-result[data-real-count]');
                    if (voyageResultElEmpty) voyageResultElEmpty.dataset.realCount = '0';

                    if (listFiltre) listFiltre.innerHTML = '';
                    updateResetFilterVisibility();
                    window.dataLayer = window.dataLayer || [];
                    if (typeof getCheckedFilters === 'function') {
                        dataLayer.push({
                            event: 'search',
                            ecommerce: {
                                currency: 'EUR',
                                filters: getCheckedFilters(window.cdvVoyagePageFilters),
                                items: []
                            },
                            _clear: true
                        });
                    }
                }

                if (listFiltre) listFiltre.innerHTML = '';
                const selectedCheckboxes = [
                    '.checkbox-pays',
                    '.checkbox-regions',
                    '.checkbox-type',
                    '.checkbox-poi',
                    '.checkbox-envie',
                    '.checkbox-villes',
                    '.checkbox-etats',
                    '.checkbox-cible'
                ];

                const allSelected = selectedCheckboxes.flatMap((selector) =>
                    Array.from(document.querySelectorAll(`${selector}:checked`)).map((el) => {
                        const name = el.getAttribute('name');
                        const labelText = el.closest('.ac-list')?.querySelector('label')?.textContent?.trim();
                        return name || labelText || String(el.value);
                    })
                );

                allSelected.forEach((name) => {
                    if (!name) return;
                    const filterDiv = document.createElement('div');
                    filterDiv.className = 'selected-filter swiper-slide';
                    filterDiv.setAttribute('data-name', name);
                    filterDiv.textContent = `${name} x`;
                    filterDiv.addEventListener('click', () => removeFilter(name));
                    if (listFiltre) listFiltre.appendChild(filterDiv);
                });

                updateResetFilterVisibility();
                if (typeof window.cdvRefreshSelectedFiltersStrip === 'function') {
                    window.cdvRefreshSelectedFiltersStrip();
                }
            })
            .catch((error) => {
                if (loaderFiltre) loaderFiltre.style.display = 'none';
                console.error("Une erreur s'est produite : ", error);
                if (noProduct) noProduct.style.display = 'block';
            });
    }

    function removeFilter(name) {
        document.querySelectorAll('.selected-filter').forEach((el) => {
            if (el.getAttribute('data-name') === name) {
                el.remove();
            }
        });

        Array.from(document.getElementsByName(name)).forEach((checkbox) => {
            checkbox.checked = false;
        });

        loadProduct();
    }

    function resetFilters() {
        if (usesAlmFilters) {
            resetAlmModalFiltersViaPlugin();
            updateResetFilterVisibility();
            if (typeof window.cdvRefreshSelectedFiltersStrip === 'function') {
                window.cdvRefreshSelectedFiltersStrip();
            }
            return;
        }

        const checkboxes = document.querySelectorAll(
            `${paysClass}, ${regionsClass}, ${typeClass}, ${poiClass}, ${envieClass}, ${villesClass}, ${etatsClass}, ${ciblesClass}`
        );
        checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
        });

        if (listFiltre) listFiltre.innerHTML = '';

        loadProduct();
        updateResetFilterVisibility();

        if (filterContainer && originalFiltersHtml) {
            filterContainer.innerHTML = '';
            filterContainer.insertAdjacentHTML('beforeend', originalFiltersHtml);
            reinitAccordion();
        }
    }

    function updateResetFilterVisibility() {
        const almPills = document.querySelectorAll('#alm-selected-filters li');
        const legacyPills = document.querySelectorAll('.selected-filter');
        const nUrl = countAlmFilterParamsInUrl();
        const enviesStrip = hasActiveEnviesStripSelection();

        const hasActiveFilters =
            almPills.length > 0 || legacyPills.length > 0 || nUrl > 0 || enviesStrip;

        let pillCount = almPills.length > 0 ? almPills.length : legacyPills.length;
        if (pillCount === 0) {
            if (nUrl > 0) {
                pillCount = nUrl;
            } else if (enviesStrip) {
                pillCount = 1;
            }
        }

        const btnFilterNoSelected = document.querySelector('.btn-filter-no-selected');
        const activeCountSpan = document.querySelector('.cdv-active-count');

        if (hasActiveFilters) {
            if (resetFilter) {
                resetFilter.style.removeProperty('display');
                resetFilter.style.removeProperty('alignItems');
                resetFilter.style.removeProperty('justifyContent');
            }
            if (swiperControls) swiperControls.classList.remove('hidden');
            if (activeCountSpan) {
                let countText;
                if (legacyPills.length > 0 && size > 0 && legacyPills.length === size) {
                    countText = 'Tous les filtres sélectionnés - ';
                } else {
                    countText = `${pillCount} filtre${pillCount > 1 ? 's' : ''} actif${pillCount > 1 ? 's' : ''} - `;
                }
                activeCountSpan.textContent = countText;
                activeCountSpan.classList.remove('hidden');
            }
        } else {
            if (resetFilter) resetFilter.style.display = 'none';
            if (swiperControls) swiperControls.classList.add('hidden');
            if (activeCountSpan) activeCountSpan.classList.add('hidden');
        }
    }

    window.cdvUpdateResetFilterVisibility = updateResetFilterVisibility;
    initFormFilters();
}
